import type { AppDispatch } from '../store';
import { sendMdtDispatch } from '../store/slices/dispatchSlice';
import type { MdtDispatch, Resource } from '../types';
import { API_BASE_URL } from './utils';

export type GeoLocationPayload = {
  latitude: number;
  longitude: number;
  address: string;
};

export type ResolveMdtFormIncidentArgs = {
  token: string;
  mdtResource: Resource | null;
  activeDispatch: MdtDispatch | null;
  reduxDispatch: AppDispatch;
  /** Used only when creating a new incident (no active assignment). */
  createTitle: string;
  createDescription: string;
  createSeverity?: string;
  createTags?: string[];
  location: GeoLocationPayload;
};

export type ResolvedMdtFormIncident = {
  incidentId: string;
  eventId: string;
  createdNew: boolean;
};

function getStoredEventId(): string {
  try {
    const eventStr = localStorage.getItem('c2_mdt_event');
    if (!eventStr) return '';
    return JSON.parse(eventStr)?.id || '';
  } catch {
    return '';
  }
}

/** Resolve GPS + reverse geocode for MDT quick-action forms. */
export async function resolveQuickActionLocation(): Promise<GeoLocationPayload> {
  let location: GeoLocationPayload = { latitude: 0, longitude: 0, address: 'Unknown' };
  if (!navigator.geolocation) return location;
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
    });
    try {
      const geocodeRes = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=18&addressdetails=1`,
        { headers: { 'User-Agent': 'C2-Command-System' } },
      );
      if (geocodeRes.ok) {
        const geocodeData = await geocodeRes.json();
        const addr = geocodeData.address || {};
        const parts = [
          addr.road || addr.street || '',
          addr.suburb || addr.district || '',
          addr.town || addr.city || addr.village || '',
        ].filter(Boolean);
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          address: parts.length > 0
            ? `GPS Location Near: ${parts.join(', ')}`
            : `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
        };
      } else {
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
        };
      }
    } catch {
      location = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
      };
    }
  } catch {
    /* ignore */
  }
  return location;
}

/** Self-assign an MDT unit to an incident (updates incident, resource, dispatch + Redux). */
export async function assignMdtResourceToIncident(args: {
  token: string;
  mdtResource: Resource;
  incident: {
    id: string;
    title?: string;
    category?: string;
    severity?: string;
  };
  eventId: string;
  location: GeoLocationPayload;
  reduxDispatch: AppDispatch;
  resourceStatus?: Resource['status'];
  dispatchNotes?: string;
}): Promise<MdtDispatch> {
  const {
    token,
    mdtResource,
    incident,
    eventId,
    location,
    reduxDispatch,
    resourceStatus = 'onscene',
    dispatchNotes = 'MDT self-assign',
  } = args;

  const callSign = mdtResource.name;

  await fetch(`${API_BASE_URL}/incidents/${incident.id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ assignedUnits: [callSign] }),
  });

  await fetch(`${API_BASE_URL}/resources/${mdtResource.id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      assignedIncident: incident.id,
      status: resourceStatus,
    }),
  });

  const now = new Date().toISOString();
  const dispatchPayload: MdtDispatch = {
    id: `DISP-MDT-${Date.now()}`,
    incidentId: incident.id,
    eventId,
    incidentTitle: incident.title || 'Incident',
    incidentCategory: incident.category || 'medical',
    incidentSeverity: (incident.severity as MdtDispatch['incidentSeverity']) || 'medium',
    incidentLocation: location,
    targetUnitId: mdtResource.id,
    targetUnitCallSign: callSign,
    dispatchedBy: dispatchNotes,
    dispatchedAt: now,
    status: 'accepted',
    respondedAt: now,
    notes: dispatchNotes,
  };

  await fetch(`${API_BASE_URL}/dispatches`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(dispatchPayload),
  });

  reduxDispatch(sendMdtDispatch(dispatchPayload));
  return dispatchPayload;
}

/**
 * Attach MDT forms (Paracetamol / Minor Injury / etc.) to the current assignment.
 * Only creates a new incident (and self-assigns the unit) when there is no accepted dispatch.
 */
export async function resolveMdtFormIncident(
  args: ResolveMdtFormIncidentArgs
): Promise<ResolvedMdtFormIncident> {
  const {
    token,
    mdtResource,
    activeDispatch,
    reduxDispatch,
    createTitle,
    createDescription,
    createSeverity = 'low',
    createTags = [],
    location,
  } = args;

  const accepted = activeDispatch?.status === 'accepted' ? activeDispatch : null;
  if (accepted?.incidentId) {
    return {
      incidentId: accepted.incidentId,
      eventId: accepted.eventId || getStoredEventId(),
      createdNew: false,
    };
  }

  const eventId = getStoredEventId();
  const incidentRes = await fetch(`${API_BASE_URL}/incidents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      eventId,
      title: createTitle,
      category: 'medical',
      severity: createSeverity,
      status: 'open',
      location,
      description: createDescription,
      tags: createTags,
    }),
  });
  if (!incidentRes.ok) throw new Error('Failed to create incident');
  const { incident } = await incidentRes.json();

  if (mdtResource) {
    await assignMdtResourceToIncident({
      token,
      mdtResource,
      incident,
      eventId,
      location,
      reduxDispatch,
      dispatchNotes: 'Created from MDT form (no prior assignment)',
    });
  }

  return {
    incidentId: incident.id,
    eventId,
    createdNew: true,
  };
}

/** Add a visible log line on the incident (Command timeline). */
export async function logFormOnIncident(
  token: string,
  incidentId: string,
  action: string,
  details: string
): Promise<void> {
  await fetch(`${API_BASE_URL}/incidents/${incidentId}/logs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action, details }),
  });
}
